import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { styled } from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import List, { ListProps } from '@mui/material/List';
import isUrlInChildren from '@fuse/core/FuseNavigation/isUrlInChildren';
import { ListItemButton } from '@mui/material';
import usePathname from '@fuse/hooks/usePathname';
import FuseNavBadge from '../../FuseNavBadge';
import FuseNavItem, { FuseNavItemComponentProps } from '../../FuseNavItem';
import FuseSvgIcon from '../../../FuseSvgIcon';
import { FuseNavItemType } from '../../types/FuseNavItemType';
import LucideIcon from '@/components/common/LucideIcon';
import type { LucideIcon as LucideIconType } from 'lucide-react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useFuseLayoutSettings from '@fuse/core/FuseLayout/useFuseLayoutSettings';
import { AccordionProvider, useAccordion } from '../AccordionContext';

type ListComponentProps = ListProps & {
	itempadding: number;
};

const Root = styled(List)<ListComponentProps>(({ theme, ...props }) => ({
	padding: 0,
	'&.open': {},
	'& > .fuse-list-item': {
		height: 50,
		width: '100%',
		borderTopLeftRadius: '8px',
		borderBottomLeftRadius: '8px',
		borderTopRightRadius: '0',
		borderBottomRightRadius: '0',
		margin: '0 0 4px 0',
		paddingRight: 32,
		paddingLeft: props.itempadding > 80 ? 80 : props.itempadding,
		paddingTop: 14,
		paddingBottom: 14,
		marginRight: '-16px',
		marginLeft: '16px',
		color: 'var(--neutral-gray-dark)',
		transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
		'&:hover': {
			color: 'var(--neutral-gray-dark)',
			backgroundColor: 'var(--neutral-bg-active)',
			'& > .fuse-list-item-icon': { color: 'var(--neutral-gray-dark)' },
			'& > .fuse-list-item-text-primary': { color: 'var(--neutral-gray-dark)' },
		},
		'&:focus': {
			color: 'var(--neutral-gray-dark)',
			backgroundColor: 'var(--neutral-bg-active) !important',
			'& > .fuse-list-item-icon': { color: 'var(--neutral-gray-dark)' },
			'& > .fuse-list-item-text-primary': { color: 'var(--neutral-gray-dark)' },
		},
		'&:active': {
			color: 'var(--neutral-gray-dark)',
			backgroundColor: 'var(--neutral-bg-active) !important',
			'& > .fuse-list-item-icon': { color: 'var(--neutral-gray-dark)' },
			'& > .fuse-list-item-text-primary': { color: 'var(--neutral-gray-dark)' },
		},
		'& > .fuse-list-item-icon': {
			marginRight: 24,
			color: 'var(--neutral-gray-dark)',
			transition: 'color 0.2s ease-in-out',
			fontSize: '16px',
			'& svg': {
				width: '16px !important',
				height: '16px !important',
			},
		},
		'& > .fuse-list-item-text-primary': {
			color: 'var(--neutral-gray-dark)',
			transition: 'color 0.2s ease-in-out',
			fontSize: '16px',
			fontWeight: '500',
		},
		'&.has-active-child:not(.open)': {
			backgroundColor: 'var(--neutral-bg-active)',
			'& > .fuse-list-item-icon': { color: 'var(--primary-500) !important' },
			'& > .fuse-list-item-text-primary': { color: 'var(--primary-500) !important' },
		},
	},
}));

function needsToBeOpened(pathname: string, item: FuseNavItemType) {
	return pathname && isUrlInChildren(item, pathname);
}

function findFirstChildUrl(item: FuseNavItemType, checkPermission?: boolean): string | undefined {
	for (const child of item.children ?? []) {
		if (child.disabled || (checkPermission && !child.hasPermission)) {
			continue;
		}

		const url = child.url || findFirstChildUrl(child, checkPermission);

		if (url) {
			return url;
		}
	}

	return undefined;
}

/**
 * FuseNavVerticalCollapse component used for vertical navigation items with collapsible children.
 */
function FuseNavVerticalCollapse(props: FuseNavItemComponentProps) {
	const pathname = usePathname();
	const { item, nestedLevel = 0, onItemClick, checkPermission } = props;
	const accordion = useAccordion();
	const [localOpen, setLocalOpen] = useState(() => needsToBeOpened(pathname, item));
	const open = accordion ? accordion.openId === item.id : localOpen;
	const setOpen = (value: boolean) => {
		if (accordion) {
			accordion.setOpenId(value ? item.id : null);
		} else {
			setLocalOpen(value);
		}
	};
	const itempadding = nestedLevel > 0 ? 48 + nestedLevel * 16 : 24;
	const navigate = useNavigate();
	const { config } = useFuseLayoutSettings();
	const isFolded = !!config?.navbar?.folded;
	const firstChildUrl = useMemo(() => findFirstChildUrl(item, checkPermission), [item, checkPermission]);
	const component = item.url ? NavLinkAdapter : 'li';
	const hasActiveChild = pathname && isUrlInChildren(item, pathname);
	const initialOpenChildId =
		item.children?.find((child) => pathname && (child.url === pathname || isUrlInChildren(child, pathname)))?.id ?? null;

	const itemProps = useMemo(
		() => ({
			...(component !== 'li' && {
				disabled: item.disabled,
				to: item.url,
				end: item.end,
				role: 'button',
				exact: item?.exact,
			}),
		}),
		[item, component],
	);

	const memoizedContent = useMemo(
		() => (
			<Root
				className={clsx(open && 'open')}
				itempadding={itempadding}
				sx={item.sx}
			>
				<ListItemButton
					component={component}
					className={clsx('fuse-list-item', open && 'open', hasActiveChild && 'has-active-child')}
					onClick={() => {
						if (isFolded && firstChildUrl) {
							navigate(firstChildUrl);
							return;
						}

						setOpen(!open);
					}}
					sx={{
						'& .MuiTouchRipple-root  .MuiTouchRipple-child': {
							color: 'var(--neutral-gray-medium)',
						},
						'&:active': {
							backgroundColor: 'var(--neutral-bg-active)',
							boxShadow: '0px 2px 1px -1px #3639412B',
						},
					}}
					{...itemProps}
				>
					{item.icon &&
						(typeof item.icon === 'string' ? (
							<FuseSvgIcon
								className={clsx('fuse-list-item-icon shrink-0', item.iconClass)}
								color="action"
							>
								{item.icon}
							</FuseSvgIcon>
						) : (
							<LucideIcon
								icon={item.icon as LucideIconType}
								className={clsx('fuse-list-item-icon shrink-0', item.iconClass)}
								size={16}
								color="inherit"
								sx={{
									width: '16px !important',
									height: '16px !important',
									minWidth: '16px !important',
									minHeight: '16px !important',
									fontSize: '16px !important',
									'& svg': {
										width: '16px !important',
										height: '16px !important',
									},
								}}
							/>
						))}

					<ListItemText
						className="fuse-list-item-text"
						primary={item.title}
						secondary={item.subtitle}
						classes={{
							primary: 'text-[16px] fuse-list-item-text-primary truncate',
							secondary: 'text-[14px] fuse-list-item-text-secondary leading-[1.5] truncate',
						}}
						sx={{
							'& .MuiListItemText-primary': {
								fontWeight: '500 !important',
								fontSize: '16px !important',
								color: 'var(--neutral-gray-dark)',
							},
							'& .MuiListItemText-secondary': {
								fontWeight: '500 !important',
								fontSize: '14px !important',
								color: 'var(--neutral-gray-dark)',
							},
							'& .fuse-list-item-text-primary': {
								fontWeight: '500 !important',
								fontSize: '16px !important',
								color: 'var(--neutral-gray-dark)',
							},
							'& .fuse-list-item-text-secondary': {
								fontWeight: '500 !important',
								fontSize: '14px !important',
								color: 'var(--neutral-gray-dark)',
							},
							'.active &': {
								'& .MuiListItemText-primary': {
									color: 'var(--primary-500) !important',
								},
								'& .fuse-list-item-text-primary': {
									color: 'var(--primary-500) !important',
								},
							},
							'.has-active-child:not(.open) &': {
								'& .MuiListItemText-primary': {
									color: 'var(--primary-500) !important',
								},
								'& .fuse-list-item-text-primary': {
									color: 'var(--primary-500) !important',
								},
							},
						}}
					/>

					{item.badge && <FuseNavBadge badge={item.badge} />}

					<IconButton
						disableRipple
						className="-mx-3 h-6 w-6 p-0 hover:bg-transparent focus:bg-transparent"
						sx={{
							marginLeft: 'auto !important',
							'&:hover': {
								backgroundColor: 'transparent !important',
							},
							'&:focus': {
								backgroundColor: 'transparent !important',
							},
							'&:active': {
								backgroundColor: 'transparent !important',
							},
						}}
						onClick={(ev) => {
							ev.preventDefault();
							ev.stopPropagation();
							setOpen(!open);
						}}
					>
						<LucideIcon
							icon={open ? ChevronDown : ChevronRight}
							className="arrow-icon"
							size={20}
							color="inherit"
						/>
					</IconButton>
				</ListItemButton>

				{item.children && (
					<Collapse
						in={!!open}
						className="collapse-children"
					>
						<AccordionProvider initialOpenId={initialOpenChildId}>
							{item.children.map((_item) => (
								<FuseNavItem
									key={_item.id}
									type={`vertical-${_item.type}`}
									item={_item}
									nestedLevel={nestedLevel + 1}
									onItemClick={onItemClick}
									checkPermission={checkPermission}
								/>
							))}
						</AccordionProvider>
					</Collapse>
				)}
			</Root>
		),
		[
			checkPermission,
			component,
			hasActiveChild,
			initialOpenChildId,
			isFolded,
			firstChildUrl,
			navigate,
			item.badge,
			item.children,
			item.icon,
			item.iconClass,
			item.subtitle,
			item.sx,
			item.title,
			itemProps,
			itempadding,
			nestedLevel,
			onItemClick,
			open,
		],
	);

	if (checkPermission && !item?.hasPermission) {
		return null;
	}

	return memoizedContent;
}

const NavVerticalCollapse = FuseNavVerticalCollapse;

export default NavVerticalCollapse;
